<script>
export const pageName = 'inventory-motors-add';
export const pageRoute = '/inventory/motors/add';
export const title = 'Add Motor(s)'
export const requireMembership = true;

import { hideLoading, showLoading } from '../../js/utils/loading.js';
import { navigate } from '@trullock/page-manager'
import { commands } from '../../js/lib/functions.js';
import { showToast } from '../../js/utils/toast.js'
import TomSelect from 'tom-select'
import { listenToMotorsInventory } from '../../js/readModel.js';
import { getCurrentUserId } from '../../js/auth.js';

export default {
	data() {
		return { 
			motorId: null,
			manufacturer: null,
			designation: null,
			name: null,
			classification: 'A',
			totalImpulse: 0,
			delay: '0',
			diameter: null,
			length: null,
			type: 'su',
			casing: null,
			propWeight: null,
			propType: null,
			quantity: 1,
			notes: null
		}
	},

	methods: {
		beforeHide() {
			return this.isDirty ? 'Are you sure you want to abandon these motors?' : null
		},
		async boot(opts) {
			
			let response = await fetch('/api/inventory/motors');
			let motors = await response.json();
			motors.forEach(m => {
				m.displayName = m.manufacturer + ' ' + m.commonName + ' - ' + m.propInfo + (m.type == 'reload' ? (m.case ? ', ' + m.case : '') : ', Single Use');
				m.disabled = false;
			})
			
			this.$motor = new TomSelect(this.$refs.motor, {
				options: motors,
				render: {
					option: function (data, escape) {
						return `<div>${escape(data.displayName)}</div>`;
					},
					item: function (item, escape) {
						return `<div>${escape(item.displayName)}</div>`;
					}
				},
				searchField: ['commonName', 'designator', 'manufacturer'],
				valueField: 'id',
				closeAfterSelect: true,
				refreshThrottle: 1,
				maxItems: 1,
				onItemAdd: (value, $item) => {
					let motor = motors.find(m => m.id == value);
					if(motor)
					{
						this.motorId = motor.id;

						this.manufacturer = motor.manufacturer;

						this.designation = motor.name;
						this.name = motor.name;

						this.classification = motor.class;
						this.totalImpulse = motor.totalImpulse;
						this.delay = motor.delay;
						
						this.diameter = motor.diameter;
						this.length = motor.length;

						this.type = motor.type;
						this.casing = motor.case;

						this.propWeight = motor.propWeight;
						this.propType = motor.propInfo;

					}
					this.$motor.clear();
				}
			});
			
			await listenToMotorsInventory(getCurrentUserId(), 'stock', inventory => {
				this.inventory = inventory;
			});
		},
		async show(opts) {
			commands.warmUp();

			this.isDirty = false;

			this.motorId = null;
			this.manufacturer = null;
			this.designation = null;
			this.name = null;
			this.classification = 'A';
			this.totalImpulse = 0;
			this.delay = '0';
			this.diameter = null;
			this.length = null;
			this.type = 'su';
			this.casing = null;
			this.propWeight = null;
			this.propType = null;
			this.quantity = 1;
			this.notes = null;

			for(let motor of Object.values(this.$motor.options))
			{
				motor.disabled = false
				// we have to do this manually because tomselect is buggy https://github.com/orchidjs/tom-select/issues/859
				motor.$div?.setAttribute('data-selectable', '')

				this.inventory.motors.forEach(m => {	
					if(motor.id == m.motorId && m.quantity > 0)
					{
						motor.disabled = true;
						// we have to do this manually because tomselect is buggy https://github.com/orchidjs/tom-select/issues/859
						motor.$div?.removeAttribute('data-selectable')
					}
				})
			}
		},
		changed() {
			this.isDirty = true;
		},
		async submit () {
			showLoading();

			let result = await commands.inventory.motors.add({
				motor: {
					motorId: this.motorId,
					manufacturer: this.manufacturer,
					designation: this.designation,
					name: this.name,
					classification: this.classification,
					totalImpulse: this.totalImpulse,
					delay: this.delay.toString(),
					diameter: this.diameter,
					length: this.length,
					type: this.type,
					casing: this.casing,
					propWeight: this.propWeight,
					propType: this.propType
				},

				quantity: this.quantity,
				notes: this.notes
			});

			if(!result.success)
			{
				showToast({ message: 'Error adding motor', style: 'bg-danger'})
				hideLoading();
				return;
			}

			this.isDirty = false;

			navigate(`/inventory/motors`)

			showToast({
				message: 'Motor(s) created',
				style: 'bg-success'
			})

			hideLoading();
		}
	}
}

</script>
<template>
	<div class="container py-5">
		<div class="row justify-content-center">
			<div class="col-12 col-md-8 col-xl-6 my-5">
				<h1 class="display-4 text-center mb-3">Add Motors</h1>
				<p class="text-muted text-center mb-5">Add motors to your inventory.</p>
					
				<form @submit.prevent="submit" @change="changed">
					<div class="form-group">
						<label class="form-label">
							Search
						</label>
						<input type="text" class="form-control" ref="motor" placeholder="e.g. M745" autofocus>
						<p class="form-text text-muted small mb-0">If you can't find a motor, please <a href="mailto:membership@ukra.org.uk?subject=Missing%20motor">let us know</a>. We don't currently support custom motors.</p>
					</div>
					<div class="row">
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Manufacturer
								</label>
								<input type="text" class="form-control" v-model="manufacturer" required readonly placeholder="e.g. Estes">
								<span class="invalid-feedback mt-2"></span>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Name
								</label>
								<input type="text" class="form-control" v-model="name" required readonly placeholder="e.g. I59">
								<span class="invalid-feedback mt-2"></span>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Impulse Class
								</label>
								<select class="form-control" v-model="classification" disabled>
									<option v-for="c of [...Array(15).keys()].map(i => String.fromCharCode(65 + i))" :value="c">{{ c }}</option>
								</select>
								<span class="invalid-feedback mt-2"></span>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Designation
								</label>
								<input type="text" class="form-control" v-model="designation" readonly placeholder="e.g. I59WN" required>
								<span class="invalid-feedback mt-2"></span>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Total impulse
								</label>
								<div class="input-group has-validation">
									<input type="number" autocomplete="off" min="0" max="999999" step="0.01" v-model="totalImpulse" readonly class="form-control" required/>
									<div class="input-group-append">
										<div class="input-group-text">
											Ns
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Delay
								</label>
								<div class="input-group has-validation">
									<input type="text" autocomplete="off" pattern="([0-9]+)|(P)" v-model="delay" class="form-control" readonly required placeholder="e.g. 6" />
									<div class="input-group-append">
										<div class="input-group-text">
											s
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
									<p class="form-text text-muted small mb-0">Either an integer or P for plugged.</p>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Diameter
								</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="999" step="1" autocomplete="off" v-model="diameter" class="form-control" readonly required placeholder="e.g. 24" />
									<div class="input-group-append">
										<div class="input-group-text">
											mm
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Length
								</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="9999" step="0.1" autocomplete="off" v-model="length" class="form-control" readonly required placeholder="e.g. 70" />
									<div class="input-group-append">
										<div class="input-group-text">
											mm
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
							</div>
						</div>
					</div>
					
					<div class="row">
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Type
								</label>
								<select class="form-control" v-model="type" disabled>
									<option value="su">Single use</option>
									<option value="reload">Reload</option>
								</select>
								<span class="invalid-feedback mt-2"></span>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Case
								</label>
								<input type="text" class="form-control" :disabled="type=='su'" v-model="casing" readonly :required="type!='su'" placeholder="e.g. Pro29-2G">
								<span class="invalid-feedback mt-2"></span>
							</div>
						</div>
					</div>

					
					<div class="row">
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Propellant weight
								</label>
								<div class="input-group has-validation">
									<input type="number" min="0.01" max="99999" step="0.01" autocomplete="off" v-model="propWeight" class="form-control" readonly required placeholder="e.g. 5" />
									<div class="input-group-append">
										<div class="input-group-text">
											g
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label class="form-label">
									Propellant type
								</label>
								<input type="text" autocomplete="off" v-model="propType" class="form-control" readonly required placeholder="e.g. Black Powder" />
								<span class="invalid-feedback"></span>
							</div>
						</div>
					</div>
					<hr class="my-5" />
					<div class="form-group">
						<label class="form-label">
							Quantity
						</label>
						<input type="number" min="0" step="1" max="9999" class="form-control" v-model="quantity" placeholder="e.g. 10">
						<span class="invalid-feedback mt-2"></span>
					</div>
					<div class="form-group">
						<label class="form-label mb-1">Notes</label>
						<textarea class="form-control" v-model="notes" rows="4"></textarea>
					</div>
					<button class="btn w-100 btn-primary">Add motors <span class="fe fe-check-circle"></span></button>
				</form>
			</div>
		</div>
    </div>
</template>
